import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/utils/api';
import { Loader2, MessageSquare, X, Sparkles, Phone, Mail, MapPin, Clock } from 'lucide-react';

const STATUS_OPTIONS = ['Neu', 'Kontaktiert', 'Angebot gesendet', 'Abgeschlossen', 'Verworfen'];

const scoreColor = (score) => {
  if (score == null) return 'bg-gray-200 text-gray-600';
  if (score >= 8) return 'bg-green-100 text-green-700 border-green-300';
  if (score >= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-red-100 text-red-700 border-red-300';
};

const fmtDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return s;
  }
};

const AdminAILeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/chat-leads'),
        api.get('/admin/chat-leads/stats/summary'),
      ]);
      setLeads(r.data || []);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (sid, status) => {
    await api.patch(`/admin/chat-leads/${sid}/status`, { status });
    setLeads((prev) => prev.map((l) => (l.session_id === sid ? { ...l, status } : l)));
    if (selected?.session_id === sid) setSelected({ ...selected, status });
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    const res = await api.post(`/admin/chat-leads/${selected.session_id}/notes`, { text: note.trim() });
    setSelected({ ...selected, notes: [...(selected.notes || []), res.data.note] });
    setNote('');
  };

  const openDetail = async (sid) => {
    try {
      const res = await api.get(`/admin/chat-leads/${sid}`);
      setSelected(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AdminLayout title="AI-Chat Leads">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Sessions gesamt" value={stats.total_sessions} />
          <StatCard label="Leads erfasst" value={stats.total_leads} highlight />
          <StatCard label="Neue Leads" value={stats.new_leads} />
          <StatCard label="Hot Leads (Score ≥ 8)" value={stats.high_score} accent />
        </div>
      )}

      {(() => {
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#4A331D]" />
            </div>
          );
        }
        if (leads.length === 0) {
          return (
            <div className="bg-white rounded-xl p-10 text-center text-gray-500 border border-dashed">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              Noch keine AI-Chat Leads erfasst.
            </div>
          );
        }
        return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-leads-table">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Score</th>
                  <th className="text-left px-4 py-3">Name / Tel.</th>
                  <th className="text-left px-4 py-3">Projekt</th>
                  <th className="text-left px-4 py-3">Dringlichkeit</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Zeit</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.session_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`ai-lead-row-${l.session_id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${scoreColor(l.ai_score)}`}>
                        {l.ai_score ?? '–'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#4A331D]">{l.lead_data?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{l.lead_data?.phone || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.ai_project_type || '-'}</div>
                      <div className="text-xs text-gray-500 line-clamp-1 max-w-[260px]">{l.ai_summary || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{l.ai_urgency || '-'}</td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status || 'Neu'}
                        onChange={(e) => updateStatus(l.session_id, e.target.value)}
                        className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                        data-testid={`ai-lead-status-${l.session_id}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(l.completed_at || l.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openDetail(l.session_id)}
                        className="text-[#4A331D] hover:underline text-xs font-medium"
                        data-testid={`ai-lead-open-${l.session_id}`}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="ai-lead-detail">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${scoreColor(selected.ai_score)}`}>
                    Score {selected.ai_score ?? '-'}/10
                  </span>
                  <span className="text-xs text-gray-500">{selected.ai_urgency}</span>
                </div>
                <h2 className="text-lg font-bold text-[#4A331D]">{selected.lead_data?.name || 'Lead'}</h2>
                <div className="text-xs text-gray-500">{selected.ai_project_type}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* AI Insights */}
              <section className="bg-gradient-to-br from-[#FAF7F2] to-white border border-[#D4AA6A]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs font-bold text-[#4A331D] mb-2">
                  <Sparkles className="w-4 h-4" /> AI-ANALYSE
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Zusammenfassung</div>
                    <div>{selected.ai_summary || '-'}</div>
                  </div>
                  {selected.ai_score_reason && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Score-Begruendung</div>
                      <div className="text-xs">{selected.ai_score_reason}</div>
                    </div>
                  )}
                  {selected.ai_next_action && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Empfohlene Aktion</div>
                      <div className="text-xs font-medium text-[#4A331D]">{selected.ai_next_action}</div>
                    </div>
                  )}
                </div>
              </section>

              {/* Contact */}
              <section className="grid grid-cols-2 gap-3 text-sm">
                <InfoLine icon={Phone} label="Telefon" value={selected.lead_data?.phone} link={`tel:${selected.lead_data?.phone}`} />
                <InfoLine icon={Mail} label="E-Mail" value={selected.lead_data?.email} link={selected.lead_data?.email ? `mailto:${selected.lead_data?.email}` : null} />
                <InfoLine icon={MapPin} label="Ort" value={selected.lead_data?.location} />
                <InfoLine icon={Clock} label="Dauer" value={selected.duration_sec != null ? `${selected.duration_sec} s` : '-'} />
              </section>

              {/* Metadata */}
              {selected.metadata && (
                <section className="text-xs text-gray-600 border rounded-lg p-3 space-y-1 bg-gray-50">
                  <div><b>Geraet:</b> {selected.metadata.device}</div>
                  <div><b>Seite:</b> {selected.metadata.page}</div>
                  <div><b>Quelle:</b> {selected.metadata.source || selected.metadata.referer || '-'}</div>
                  {selected.metadata.utm_campaign && <div><b>Kampagne:</b> {selected.metadata.utm_campaign}</div>}
                </section>
              )}

              {/* Chat history */}
              <section>
                <div className="text-xs font-bold text-[#4A331D] mb-2">CHATVERLAUF</div>
                <div className="border rounded-xl p-3 space-y-2 max-h-[400px] overflow-y-auto bg-[#FAF7F2]">
                  {(selected.messages || []).map((m, i) => (
                    <div key={m.ts || `${m.role}-${i}-${m.content?.slice(0, 16)}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.role === 'user' ? 'bg-[#4A331D] text-white' : 'bg-white border'}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Notes */}
              <section>
                <div className="text-xs font-bold text-[#4A331D] mb-2">NOTIZEN</div>
                {(selected.notes || []).map((n, i) => (
                  <div key={n.timestamp || `${n.author}-${i}`} className="text-xs bg-yellow-50 border border-yellow-100 rounded p-2 mb-2">
                    <div>{n.text}</div>
                    <div className="text-[10px] text-gray-500 mt-1">{n.author} – {fmtDate(n.timestamp)}</div>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Notiz hinzufuegen..."
                    className="flex-1 border rounded px-3 py-2 text-sm"
                    data-testid="ai-lead-note-input"
                  />
                  <button onClick={addNote} className="bg-[#4A331D] text-white text-sm px-4 rounded" data-testid="ai-lead-note-add">
                    Hinzufuegen
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const StatCard = ({ label, value, highlight, accent }) => (
  <div className={`rounded-xl p-4 border ${highlight ? 'bg-[#4A331D] text-white border-[#4A331D]' : accent ? 'bg-[#D4AA6A]/20 border-[#D4AA6A]/40' : 'bg-white border-gray-200'}`}>
    <div className={`text-xs ${highlight ? 'text-white/70' : 'text-gray-500'}`}>{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

const InfoLine = ({ icon: Icon, label, value, link }) => (
  <div>
    <div className="text-[10px] uppercase text-gray-500 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
    {link ? (
      <a href={link} className="text-sm text-[#4A331D] font-medium hover:underline">{value || '-'}</a>
    ) : (
      <div className="text-sm font-medium">{value || '-'}</div>
    )}
  </div>
);

export default AdminAILeads;
